import {
  useCallback,
  useEffect,
  useLayoutEffect,
  useRef,
  useState,
  type PointerEvent as ReactPointerEvent,
  type ReactElement,
  type WheelEvent as ReactWheelEvent,
} from 'react';
import type { FileHandleRef, PlatformBridge } from '@slate/core';
import { Maximize2, Minus, Plus } from 'lucide-react';
import { imageMimeType } from '../util/fileType';
import { modPressed } from '../keymap/platform';

export interface ImageViewerProps {
  platform: PlatformBridge;
  /** The image doc's ref (its `path`/`id` and display `name`). */
  refOrPath: FileHandleRef;
  /**
   * Optional pre-resolved URL. When set, the viewer renders this URL directly
   * (no `platform.readBinary` round-trip). Used by the lightbox to reuse
   * the same zoom/pan UI with an asset URL already resolved by the rendered
   * preview's image rewriter.
   */
  directUrl?: string;
}

/** Clamp the user's zoom to a sane range. 100% = native size; below FIT_KEY
 *  is computed dynamically so the viewer never paints smaller than fit. */
const ZOOM_MIN = 0.1;
const ZOOM_MAX = 16;
/** Step factor for wheel + button zooms — feels smooth at ~1.1× per tick. */
const ZOOM_STEP = 1.1;

/**
 * Read-only image viewer with zoom + pan controls. Reads the file's bytes via
 * the platform bridge (path-validated), wraps them in a Blob, and renders via
 * `<img>` (object URL, revoked on unmount).
 *
 * Interaction model:
 *   - Default zoom = "fit": image always fills the pane proportionally (the
 *     mode auto-recomputes on container resize, so the fit always tracks the
 *     pane).
 *   - Plain wheel / two-finger scroll = pan (clamped per-axis to the part of
 *     the image clipped by the pane). When the image fits both axes the
 *     event is left to bubble.
 *   - Cmd/Ctrl + wheel = zoom around the cursor. Trackpad pinch surfaces as
 *     a wheel event with `ctrlKey=true` (browser synthesizes this for
 *     `gesture` events), so pinch zooms naturally through the same branch.
 *   - Drag = pan when the image exceeds the pane on the relevant axis; on
 *     axes where the image fits, pan is locked (the image stays centered).
 *   - Toolbar: zoom out / fit / 100% / zoom in. The active state is
 *     highlighted so the user can tell whether they're in Fit mode (image
 *     auto-scales on resize) or a manual zoom (frozen scale).
 *   - Spacebar toggle is intentionally NOT bound to avoid hijacking
 *     scroll-spacebar in adjacent panes.
 *
 * Security note: SVGs render as static `<img>` (not inline DOM), so any
 * script embedded in the SVG can't execute under this loading mode.
 */
export function ImageViewer({
  platform,
  refOrPath,
  directUrl,
}: ImageViewerProps): ReactElement {
  const path = refOrPath.path ?? refOrPath.id;
  const name = refOrPath.name;

  const [url, setUrl] = useState<string | null>(directUrl ?? null);
  const [error, setError] = useState<string | null>(null);
  const [natural, setNatural] = useState<{ w: number; h: number } | null>(null);

  // ───────── Source bytes → object URL ─────────
  useEffect(() => {
    // Direct-URL mode: skip the read; just point the <img> at the URL.
    if (directUrl) {
      setUrl(directUrl);
      setError(null);
      setNatural(null);
      return;
    }
    let alive = true;
    let objectUrl: string | null = null;
    setUrl(null);
    setError(null);
    setNatural(null);

    void (async () => {
      try {
        const bytes = await platform.readBinary(path);
        if (!alive) return;
        const buf = new ArrayBuffer(bytes.byteLength);
        new Uint8Array(buf).set(bytes);
        const blob = new Blob([buf], { type: imageMimeType(name) });
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
      } catch (err) {
        if (!alive) return;
        setError(err instanceof Error ? err.message : 'Could not read image');
      }
    })();

    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [platform, path, name, directUrl]);

  // ───────── Viewport + zoom/pan state ─────────
  const containerRef = useRef<HTMLDivElement | null>(null);
  const imgRef = useRef<HTMLImageElement | null>(null);

  /** Container dimensions, observed for resize-driven fit recompute. */
  const [viewport, setViewport] = useState<{ w: number; h: number }>({ w: 0, h: 0 });

  /** `null` = "Fit" mode (auto-scale on resize). A number = explicit zoom factor. */
  const [zoom, setZoom] = useState<number | null>(null);
  /** Pan offset in CSS pixels, applied after centering. {x:0,y:0} = centered. */
  const [pan, setPan] = useState<{ x: number; y: number }>({ x: 0, y: 0 });

  // Observe container resize so fit-mode tracks the pane.
  useLayoutEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const update = (): void => {
      const r = el.getBoundingClientRect();
      setViewport({ w: r.width, h: r.height });
    };
    update();
    if (typeof ResizeObserver === 'undefined') return;
    const ro = new ResizeObserver(update);
    ro.observe(el);
    return () => ro.disconnect();
  }, []);

  // Compute the fit-scale (largest scale that keeps the image inside the
  // pane on both axes).
  const fitScale = (() => {
    if (!natural || viewport.w <= 0 || viewport.h <= 0) return 1;
    return Math.min(viewport.w / natural.w, viewport.h / natural.h, 1);
  })();

  /** The effective scale to apply right now. */
  const effectiveScale = zoom ?? fitScale;

  /** Painted image dimensions at the effective scale. */
  const painted = natural
    ? { w: natural.w * effectiveScale, h: natural.h * effectiveScale }
    : { w: 0, h: 0 };

  /**
   * Maximum pan offset on each axis. The image is centered at pan=0; offset
   * can travel up to (painted - viewport) / 2 in either direction before a
   * further pan would expose empty pane.
   */
  const panLimit = {
    x: Math.max(0, (painted.w - viewport.w) / 2),
    y: Math.max(0, (painted.h - viewport.h) / 2),
  };

  /** Clamp a candidate pan to the panLimit. */
  const clampPan = useCallback(
    (p: { x: number; y: number }) => ({
      x: Math.min(panLimit.x, Math.max(-panLimit.x, p.x)),
      y: Math.min(panLimit.y, Math.max(-panLimit.y, p.y)),
    }),
    [panLimit.x, panLimit.y],
  );

  // Re-clamp pan when the zoom or viewport shrinks below previous limits
  // (e.g. user zooms out → image now fits → pan should snap back to center).
  useEffect(() => {
    setPan((p) => clampPan(p));
  }, [clampPan]);

  // ───────── Zoom controls ─────────

  /**
   * Apply a new zoom factor while keeping the point under `(focusX, focusY)`
   * (container coords) visually stationary. Without this, wheel-zoom would
   * always center on the pane, which is disorienting on a panned image.
   */
  const zoomAt = useCallback(
    (factor: number, focusX: number, focusY: number) => {
      if (!natural) return;
      const oldScale = zoom ?? fitScale;
      const next = Math.max(ZOOM_MIN, Math.min(ZOOM_MAX, oldScale * factor));
      // The image's top-left corner in container coords (centered + pan).
      const cx = viewport.w / 2 + pan.x;
      const cy = viewport.h / 2 + pan.y;
      // Focus point relative to the image center, in image-pixel space.
      const ix = (focusX - cx) / oldScale;
      const iy = (focusY - cy) / oldScale;
      // After zoom, that same image-pixel point should land back at focus.
      const newCx = focusX - ix * next;
      const newCy = focusY - iy * next;
      const nextPan = { x: newCx - viewport.w / 2, y: newCy - viewport.h / 2 };
      setZoom(next);
      setPan(clampPan(nextPan));
    },
    [natural, zoom, fitScale, viewport.w, viewport.h, pan.x, pan.y, clampPan],
  );

  const zoomIn = useCallback(() => {
    const cx = viewport.w / 2;
    const cy = viewport.h / 2;
    zoomAt(ZOOM_STEP, cx, cy);
  }, [viewport.w, viewport.h, zoomAt]);

  const zoomOut = useCallback(() => {
    const cx = viewport.w / 2;
    const cy = viewport.h / 2;
    zoomAt(1 / ZOOM_STEP, cx, cy);
  }, [viewport.w, viewport.h, zoomAt]);

  const fit = useCallback(() => {
    setZoom(null);
    setPan({ x: 0, y: 0 });
  }, []);

  const actual = useCallback(() => {
    setZoom(1);
    setPan({ x: 0, y: 0 });
  }, []);

  const onWheel = useCallback(
    (e: ReactWheelEvent<HTMLDivElement>) => {
      // Zoom branch: Cmd-wheel (mac) / Ctrl-wheel (win/linux) / trackpad
      // pinch (browser sets ctrlKey on the synthesized wheel event). Use a
      // smooth log-space step capped at one ZOOM_STEP per tick so a chunky
      // mouse wheel doesn't blow past the cursor focus.
      if (e.ctrlKey || e.metaKey) {
        e.preventDefault();
        const rect = containerRef.current?.getBoundingClientRect();
        if (!rect) return;
        const focusX = e.clientX - rect.left;
        const focusY = e.clientY - rect.top;
        const raw = Math.exp(-e.deltaY * 0.01);
        const factor = Math.max(1 / ZOOM_STEP, Math.min(ZOOM_STEP, raw));
        zoomAt(factor, focusX, focusY);
        return;
      }
      // Default branch: pan. If the image fits both axes there's nothing to
      // pan — let the event bubble so an outer pane can scroll.
      if (panLimit.x === 0 && panLimit.y === 0) return;
      e.preventDefault();
      setPan((p) =>
        clampPan({
          x: p.x - e.deltaX,
          y: p.y - e.deltaY,
        }),
      );
    },
    [zoomAt, panLimit.x, panLimit.y, clampPan],
  );

  // ───────── Pan via pointer drag ─────────
  const dragStart = useRef<{ x: number; y: number; pan: { x: number; y: number } } | null>(null);

  const onPointerDown = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      if (e.button !== 0) return; // only the primary button
      if (panLimit.x === 0 && panLimit.y === 0) return; // image fits — no drag
      dragStart.current = { x: e.clientX, y: e.clientY, pan };
      e.currentTarget.setPointerCapture(e.pointerId);
    },
    [pan, panLimit.x, panLimit.y],
  );

  const onPointerMove = useCallback(
    (e: ReactPointerEvent<HTMLDivElement>) => {
      const start = dragStart.current;
      if (!start) return;
      const dx = e.clientX - start.x;
      const dy = e.clientY - start.y;
      setPan(
        clampPan({
          x: start.pan.x + dx,
          y: start.pan.y + dy,
        }),
      );
    },
    [clampPan],
  );

  const onPointerUp = useCallback((e: ReactPointerEvent<HTMLDivElement>) => {
    if (dragStart.current) {
      dragStart.current = null;
      try {
        e.currentTarget.releasePointerCapture(e.pointerId);
      } catch {
        /* releasePointerCapture can throw if the capture's already released */
      }
    }
  }, []);

  // ───────── Keyboard zoom (Cmd/Ctrl-+ / Cmd/Ctrl-- / Cmd/Ctrl-0) ─────────
  useEffect(() => {
    if (typeof window === 'undefined') return;
    const onKey = (e: KeyboardEvent): void => {
      const root = containerRef.current;
      if (!root) return;
      const active = document.activeElement;
      if (!(active instanceof Node) || !root.contains(active)) return;
      if (!modPressed(e)) return;
      if (e.key === '=' || e.key === '+') {
        e.preventDefault();
        zoomIn();
      } else if (e.key === '-' || e.key === '_') {
        e.preventDefault();
        zoomOut();
      } else if (e.key === '0') {
        e.preventDefault();
        fit();
      } else if (e.key === '1') {
        e.preventDefault();
        actual();
      }
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [zoomIn, zoomOut, fit, actual]);

  // Reset the view when the file itself changes (different image opened).
  useEffect(() => {
    setZoom(null);
    setPan({ x: 0, y: 0 });
  }, [path]);

  // ───────── Render ─────────
  const isFitMode = zoom === null;
  const zoomLabel = isFitMode
    ? `Fit ${Math.round(fitScale * 100)}%`
    : `${Math.round(effectiveScale * 100)}%`;
  const canPan = panLimit.x > 0 || panLimit.y > 0;
  const cursor = canPan ? (dragStart.current ? 'grabbing' : 'grab') : 'default';

  return (
    <div className="mp-image-viewer" tabIndex={-1}>
      <div
        ref={containerRef}
        className="mp-image-viewer__stage"
        onWheel={onWheel}
        onPointerDown={onPointerDown}
        onPointerMove={onPointerMove}
        onPointerUp={onPointerUp}
        onPointerCancel={onPointerUp}
        style={{ cursor }}
      >
        {error ? (
          <pre className="mp-embed__error">Image error: {error}</pre>
        ) : url ? (
          <img
            ref={imgRef}
            className="mp-image-viewer__img"
            src={url}
            alt={name}
            draggable={false}
            onLoad={(e) => {
              const target = e.currentTarget;
              setNatural({ w: target.naturalWidth, h: target.naturalHeight });
            }}
            style={{
              width: painted.w || 'auto',
              height: painted.h || 'auto',
              transform: `translate3d(${pan.x}px, ${pan.y}px, 0)`,
            }}
          />
        ) : (
          <div className="mp-image-viewer__loading">Loading image…</div>
        )}
      </div>

      {/* Floating control bar — bottom-center; survives toolbar/sidebar
          changes because it's scoped to the viewer pane. */}
      {url && natural && (
        <div className="mp-image-viewer__controls" role="toolbar" aria-label="Image controls">
          <button
            type="button"
            className="mp-iconbtn"
            aria-label="Zoom out"
            title="Zoom out"
            onClick={zoomOut}
          >
            <Minus size={14} aria-hidden />
          </button>
          <button
            type="button"
            className="mp-iconbtn"
            aria-pressed={isFitMode}
            aria-label="Fit to view"
            title="Fit to view"
            onClick={fit}
          >
            <Maximize2 size={13} aria-hidden />
          </button>
          <button
            type="button"
            className="mp-image-viewer__pct"
            aria-pressed={!isFitMode && effectiveScale === 1}
            aria-label="Actual size"
            title="Actual size (100%)"
            onClick={actual}
          >
            {zoomLabel}
          </button>
          <button
            type="button"
            className="mp-iconbtn"
            aria-label="Zoom in"
            title="Zoom in"
            onClick={zoomIn}
          >
            <Plus size={14} aria-hidden />
          </button>
        </div>
      )}
    </div>
  );
}
